import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';
import { formatDate } from '@utils/format';
import { useApproveObservation, useObservationStagingRecord } from '../hooks';
import type { ApproveStagingPayload } from '../api';
import { SeriesPicker } from './SeriesPicker';
import { SourcePreview } from './SourcePreview';
import { WhatsAppOrigin } from './cells/WhatsAppOrigin';
import { ResolutionBadge } from './cells/ResolutionBadge';
import type { StagingRow } from '../schema';

const RUPEE = '₹';

interface Props {
  open: boolean;
  row: StagingRow | null;
  onClose: () => void;
}

type PriceField =
  | 'minPrice'
  | 'modalPrice'
  | 'maxPrice'
  | 'commodityArrivals'
  | 'commodityTraded';

// Prices carry a rupee adornment; quantities carry the unit. Parsing iterates
// the union of both.
const PRICE_FIELDS: { key: PriceField; label: string }[] = [
  { key: 'minPrice', label: 'Min' },
  { key: 'modalPrice', label: 'Modal' },
  { key: 'maxPrice', label: 'Max' },
];
const QTY_FIELDS: { key: PriceField; label: string }[] = [
  { key: 'commodityArrivals', label: 'Arrivals' },
  { key: 'commodityTraded', label: 'Traded' },
];
const ALL_FIELDS = [...PRICE_FIELDS, ...QTY_FIELDS];

type FormState = Record<PriceField, string> & { priceDate: string };

const toInput = (v: number | null): string => (v == null ? '' : String(v));

function buildDefaults(row: StagingRow | null): FormState {
  return {
    minPrice: toInput(row?.minPrice ?? null),
    modalPrice: toInput(row?.modalPrice ?? null),
    maxPrice: toInput(row?.maxPrice ?? null),
    commodityArrivals: toInput(row?.commodityArrivals ?? null),
    commodityTraded: toInput(row?.commodityTraded ?? null),
    priceDate: row?.priceDate ?? '',
  };
}

export function ApproveDialog({ open, row, onClose }: Props) {
  const approveMutation = useApproveObservation();
  const [form, setForm] = useState<FormState>(buildDefaults(null));
  const [mappedSeriesId, setMappedSeriesId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const needsMapping = row != null && row.resolutionStatus !== 'RESOLVED';

  // Already-published rows (Published view) open here read-only: the approve
  // endpoint short-circuits terminal rows and ignores the body, so an editable
  // form would silently discard changes behind a success toast.
  const readOnly = row != null && row.status !== 'PENDING';

  // The list row carries the pipeline's normalized text; the verbatim WhatsApp
  // message is only on the detail record, so fetch it by id when the dialog opens.
  const detail = useObservationStagingRecord(row?.id ?? null, open);
  const messageRow = detail.data ?? row;
  const isTextMessage = (messageRow?.sourceMimeType ?? '').startsWith('text/');
  // Only text messages differ between list and detail; wait for the verbatim
  // text rather than flashing the normalized version. Photos need no wait.
  const messageLoading = open && isTextMessage && detail.isPending;

  useEffect(() => {
    if (!open || !row) return;
    setForm(buildDefaults(row));
    setMappedSeriesId(row.priceSeriesId);
    setError(null);
  }, [open, row]);

  const effectiveSeriesId = needsMapping
    ? mappedSeriesId
    : row?.priceSeriesId ?? null;

  const setField = (key: keyof FormState, v: string) =>
    setForm((f) => ({ ...f, [key]: v }));

  // Parse a price input: '' -> undefined (omit -> keep pipeline value),
  // otherwise a number; NaN sentinel surfaces as a validation error.
  const parsed = useMemo(() => {
    const out: Partial<Record<PriceField, number>> = {};
    let bad: string | null = null;
    for (const { key, label } of ALL_FIELDS) {
      const raw = form[key].trim();
      if (raw === '') continue;
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) {
        bad = bad ?? `${label} must be a non-negative number`;
        continue;
      }
      out[key] = n;
    }
    return { values: out, bad };
  }, [form]);

  const submitting = approveMutation.isPending;

  const validate = (): ApproveStagingPayload | null => {
    if (!row) return null;
    if (parsed.bad) {
      setError(parsed.bad);
      return null;
    }
    const { minPrice, modalPrice, maxPrice } = parsed.values;
    if (minPrice == null && modalPrice == null && maxPrice == null) {
      setError('Enter at least one of Min, Modal, or Max price');
      return null;
    }
    if (minPrice != null && maxPrice != null && minPrice > maxPrice) {
      setError('Min cannot exceed Max');
      return null;
    }
    if (minPrice != null && modalPrice != null && minPrice > modalPrice) {
      setError('Min cannot exceed Modal');
      return null;
    }
    if (modalPrice != null && maxPrice != null && modalPrice > maxPrice) {
      setError('Modal cannot exceed Max');
      return null;
    }
    if (!form.priceDate) {
      setError('Price date is required');
      return null;
    }
    if (!effectiveSeriesId) {
      setError('Map the row to a price series before approving');
      return null;
    }
    setError(null);
    return {
      priceSeriesId: effectiveSeriesId,
      priceDate: form.priceDate,
      ...parsed.values,
    };
  };

  const onSubmit = async () => {
    const payload = validate();
    if (!row || !payload) return;
    try {
      await approveMutation.mutateAsync({ id: row.id, payload });
      onClose();
    } catch {
      // Toast shown by the mutation's onError (incl. server write-window error).
    }
  };

  const confidence =
    row?.extractionConfidence == null
      ? null
      : `${Math.round(row.extractionConfidence * 100)}%`;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="md"
      fullWidth
      aria-labelledby="approve-staging-title"
    >
      <DialogTitle
        id="approve-staging-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}
      >
        Review &amp; approve
        <WhatsAppOrigin />
        {row && (
          <span className="ml-auto text-[12px] font-normal text-foreground-muted">
            {row.refCode}
          </span>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {row && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {readOnly && (
              <div className="rounded-md bg-surface-muted px-3 py-2 text-[12.5px] text-foreground-muted ring-1 ring-border md:col-span-2">
                This reading is already <b>{row.status.toLowerCase()}</b> and is
                shown read-only. To change a published price, edit it from the
                Market prices grid.
              </div>
            )}
            {/* Source pane — the original photo/PDF/text, plus the exact WhatsApp
                message text (verbatim, unmodified) so prices can be verified. */}
            <div className="flex flex-col gap-3">
              <div className="flex min-h-[240px] items-center justify-center overflow-hidden rounded-lg bg-surface-muted ring-1 ring-border">
                {messageLoading ? (
                  <div className="flex flex-col items-center gap-2 text-foreground-muted">
                    <CircularProgress size={22} />
                    <span className="text-[12px]">Loading message…</span>
                  </div>
                ) : (
                  <SourcePreview
                    url={messageRow?.sourcePhotoUrl ?? null}
                    mimeType={messageRow?.sourceMimeType ?? null}
                    text={messageRow?.sourceText ?? null}
                  />
                )}
              </div>
              {/* Message text alongside a photo/PDF source. For text-only
                  sources SourcePreview already shows it, so don't duplicate. */}
              {!messageLoading &&
                messageRow?.sourceText &&
                !(messageRow.sourceMimeType ?? '').startsWith('text/') && (
                  <div className="rounded-lg bg-surface-muted ring-1 ring-border">
                    <div className="border-b border-border px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                      Extracted text
                    </div>
                    <pre className="max-h-[160px] overflow-auto whitespace-pre-wrap px-3 py-2 font-sans text-[12.5px] leading-relaxed text-foreground">
                      {messageRow.sourceText}
                    </pre>
                  </div>
                )}
            </div>

            {/* Numbers + mapping. */}
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-lg bg-surface-muted px-3 py-2.5 text-[12.5px]">
                <Meta label="Received" value={formatDate(row.receivedAt)} />
                <Meta label="Confidence" value={confidence ?? '—'} />
                <Meta
                  label="Crop / Variety"
                  value={
                    row.cropName
                      ? `${row.cropName} · ${row.varietyName ?? '—'}`
                      : row.cropText ?? '—'
                  }
                />
                <Meta label="Mandi" value={row.apmcName ?? row.apmcText ?? '—'} />
                <div className="col-span-2 flex items-center gap-2 pt-0.5">
                  <span className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                    Resolution
                  </span>
                  <ResolutionBadge status={row.resolutionStatus} />
                </div>
              </div>

              {needsMapping && !readOnly && (
                <SeriesPicker
                  value={mappedSeriesId}
                  onChange={setMappedSeriesId}
                  cropText={row.cropText}
                  varietyText={row.varietyText}
                  apmcText={row.apmcText}
                />
              )}

              <TextField
                label="Price date"
                type="date"
                size="small"
                value={form.priceDate}
                onChange={(e) => setField('priceDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={readOnly}
                fullWidth
              />

              <FieldGroup title={`Prices (per ${row.unit})`}>
                <div className="grid grid-cols-3 gap-3">
                  {PRICE_FIELDS.map(({ key, label }) => (
                    <TextField
                      key={key}
                      label={label}
                      size="small"
                      type="number"
                      slotProps={{
                        htmlInput: {
                          step: '0.01',
                          min: '0',
                          inputMode: 'decimal',
                        },
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              {RUPEE}
                            </InputAdornment>
                          ),
                        },
                      }}
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                      disabled={readOnly}
                      fullWidth
                    />
                  ))}
                </div>
              </FieldGroup>

              <FieldGroup title="Quantities">
                <div className="grid grid-cols-2 gap-3">
                  {QTY_FIELDS.map(({ key, label }) => (
                    <TextField
                      key={key}
                      label={label}
                      size="small"
                      type="number"
                      slotProps={{
                        htmlInput: {
                          step: '0.01',
                          min: '0',
                          inputMode: 'decimal',
                        },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              {row.unit}
                            </InputAdornment>
                          ),
                        },
                      }}
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                      disabled={readOnly}
                      fullWidth
                    />
                  ))}
                </div>
              </FieldGroup>

              {row.validationFailures.length > 0 && (
                <div className="rounded-md bg-error/[0.08] px-3 py-2 text-[12px] text-error">
                  Pipeline flags: {row.validationFailures.join(', ')}
                </div>
              )}

              {!readOnly && (
                <p className="text-[12px] text-foreground-muted">
                  At least one price is required (partial/max-only is allowed).
                  Publishing updates today's price for this series — the server
                  enforces a 3-day (IST) write window.
                </p>
              )}

              {error && <div className="text-[12.5px] text-error">{error}</div>}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            color="success"
            onClick={onSubmit}
            disabled={submitting || (needsMapping && !effectiveSeriesId)}
          >
            {submitting ? 'Approving…' : 'Approve & publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
        {label}
      </div>
      <div className="font-medium text-foreground">{value}</div>
    </div>
  );
}

function FieldGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
        {title}
      </span>
      {children}
    </div>
  );
}
